'use client';

import { useState } from 'react';
import { AlertTriangle, ChevronDown, RotateCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/features/auth';
import { cn } from '@/lib/utils';

import { useRetryOcrMutation } from '../services/ocr.mutations';
import type { OcrErrorCode } from '../types';

interface OcrErrorCardProps {
  resultId: number;
  errorCode: OcrErrorCode | null;
  errorMessage: string | null;
  /** Compact layout for sheets — collapses padding + title. */
  compact?: boolean;
  className?: string;
}

const ERROR_KEY: Record<OcrErrorCode, string> = {
  no_images: 'errors.no_images',
  template_missing: 'errors.template_missing',
  ai_quota: 'errors.ai_quota',
  ai_timeout: 'errors.ai_timeout',
  ai_safety: 'errors.ai_safety',
  ai_refused: 'errors.ai_refused',
  client_model_auth: 'errors.client_model_auth',
  client_model_quota: 'errors.client_model_quota',
  client_model_timeout: 'errors.client_model_timeout',
  client_model_unreachable: 'errors.client_model_unreachable',
  client_model_bad_response: 'errors.client_model_bad_response',
  client_model_redirect_refused: 'errors.client_model_redirect_refused',
  client_model_blocked_destination: 'errors.client_model_blocked_destination',
  client_model_bad_config: 'errors.client_model_bad_config',
  parse_failed: 'errors.parse_failed',
  superseded: 'errors.superseded',
  unknown: 'errors.unknown'
};

export function OcrErrorCard({
  resultId,
  errorCode,
  errorMessage,
  compact,
  className
}: OcrErrorCardProps) {
  const t = useTranslations('ocr');
  const [showDetail, setShowDetail] = useState(false);
  const retry = useRetryOcrMutation();

  const friendly = errorCode ? t(ERROR_KEY[errorCode]) : t('errors.unknown');

  return (
    <div
      className={cn(
        'border-destructive/40 bg-destructive/5 flex w-full flex-col gap-2 rounded-md border',
        compact ? 'p-2.5' : 'p-3',
        className
      )}
    >
      <div className='flex items-start gap-2'>
        <AlertTriangle className='text-destructive mt-0.5 h-4 w-4 shrink-0' />
        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <p className='text-destructive text-sm leading-tight font-medium'>
            {friendly}
          </p>
          {errorMessage && (
            <button
              type='button'
              onClick={() => setShowDetail((s) => !s)}
              className='text-destructive/80 hover:text-destructive flex w-fit items-center gap-1 text-[11px] transition-colors'
            >
              {showDetail ? t('hideTechnicalDetail') : t('showTechnicalDetail')}
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform',
                  showDetail && 'rotate-180'
                )}
              />
            </button>
          )}
          {showDetail && errorMessage && (
            <p className='bg-destructive/10 text-destructive/90 mt-1 rounded px-2 py-1.5 font-mono text-[11px] leading-relaxed break-words'>
              {errorMessage}
            </p>
          )}
        </div>
      </div>
      <PermissionGate permissions={['ocr.edit']}>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive w-fit gap-1.5'
          disabled={retry.isPending}
          onClick={() => retry.mutate(resultId)}
        >
          <RotateCcw
            className={cn('h-3.5 w-3.5', retry.isPending && 'animate-spin')}
          />
          {retry.isPending ? t('retrying') : t('retry')}
        </Button>
      </PermissionGate>
    </div>
  );
}
